/// <amd-module name="Dashboards/Grid/GridPlacement" />
import * as React from "react";
import { GridItemElement, GridItemPosition } from "./DashboardGridItem";

/**
 * Finds vacancies between placed grid items and assigns positions to copies of unplaced grid items.
 * Returns the original array if there were no items to place.
 * @param items - The grid items to evaluate (placed and unplaced)
 * @param wrappingColumn - The maximum column an unplaced grid item may be placed (considers column span in evaluation)
 */
export function assignPositionsToGridItems(items: ReadonlyArray<GridItemElement>, wrappingColumn: number): GridItemElement[] {
    if (wrappingColumn > 32 || wrappingColumn < 1) {
        throw Error("Cannot search grid exceeding 32 columns or less than 1 column");
    }

    const placedItems = items.slice();
    const positions: GridItemPosition[] = placedItems.map(item => item.props);

    let placedAnItem = false;
    let grid = createFilledGridCellsBitsArray(positions);
    for (let i = 0; i < positions.length; ++i) {
        const position = positions[i];
        if (!isPositionOnGrid(position)) {
            const newPosition = assignFirstAvailablePosition(position, grid, wrappingColumn);
            placedItems[i] = React.cloneElement(items[i], newPosition);
            grid = fillGridWithPosition(newPosition, grid);
            placedAnItem = true;
        }
    }

    return placedAnItem ? placedItems : (items as GridItemElement[]);
}

/**
 * Searches grid for a location that will fit the given row and column spans.
 * Algorithm moves from top-to-bottom, left-to-right.
 * Returns a copy of the given position with updated row/column.
 * @param position - The position to copy with updated row/column
 * @param grid - The bit field grid to search
 * @param wrappingColumn - The ending column to move to the next row to search
 */
function assignFirstAvailablePosition(position: Readonly<GridItemPosition>, grid: number[], wrappingColumn: number): GridItemPosition {
    let vacancy: { row: number; column: number } | undefined;
    const endingColumn = wrappingColumn - position.columnSpan;

    // Only search if the span doesn't exceed the wrapping column
    if (endingColumn > 0) {
        const endingRowIndex = grid.length;

        // A bit mask representation of the columns a given column would span on a row.
        // For example, a column span of 3 would produce a bit mask of 0b111 === 7.
        const rowMask = computeRowMask(position.columnSpan, 0);

        for (let rowIndex = 0; rowIndex < endingRowIndex; ++rowIndex) {
            // The row mask will be left-shifted one bit for each iteration of the column search loop
            // so the bits line up when checking if the column span would fit in a vacancy.
            // For example, for a column span of 3 (row mask of 0b111), and columnIndex 2, the shifted row mask would be 0b11100
            // so the bitwise & operator can be used with it and the bits of the grid at rowIndex to determine
            // if there is room for the column span to fit.
            let shiftedRowMask = rowMask;
            for (let columnIndex = 0; columnIndex <= endingColumn; ++columnIndex) {
                let spanFits = true;

                // Check row by row that the span would fit at this position
                const endingCheckRowIndex = rowIndex + position.rowSpan;
                for (let checkRowIndex = rowIndex; checkRowIndex < endingCheckRowIndex && checkRowIndex < endingRowIndex; ++checkRowIndex) {
                    // Bitwise & the shifted row mask and the grid row.
                    // If there is no overlap (i.e. the span fits) then 0 will be produced.
                    spanFits = (grid[checkRowIndex] & shiftedRowMask) === 0;
                    if (!spanFits) {
                        break;
                    }
                }

                // Found a vacancy. Capture the coordinates.
                if (spanFits) {
                    vacancy = { row: rowIndex + 1, column: columnIndex + 1 };
                    break;
                }

                shiftedRowMask *= 2; // Move the mask one column
            }

            // Found a vacancy. Don't need to continue searching.
            if (vacancy != null) {
                break;
            }
        }
    }

    // Assign the position to a new row on the grid
    if (vacancy == null) {
        vacancy = { row: grid.length + 1, column: 1 };
    }

    return {
        ...position,
        row: vacancy.row,
        column: vacancy.column
    };
}

/**
 * If a grid item position has column and row set to 0, that indicates the grid item is
 * "unplaced" meaning it does not have a location on the grid yet.
 * @param position The position of a grid item
 */
function isPositionOnGrid(position: GridItemPosition): boolean {
    return position.column > 0 && position.row > 0;
}

/**
 * Creates a bit mask representing the cells in a row a given span would occupy at a given column index.
 * For example, a span of 2 at column index 3 would return a mask value of 0b11000 === 24
 * @param columnSpan - The number of columns covered
 * @param columnIndex - The 0-indexed column (so index 0 corresponds with grid position 1)
 */
function computeRowMask(columnSpan: number, columnIndex: number): number {
    return ~(-1 * Math.pow(2, columnSpan)) * Math.pow(2, columnIndex);
}

function fillGridWithPosition(position: GridItemPosition, grid: ReadonlyArray<number>): number[] {
    const newGrid = grid.slice();

    const columnIndex = position.column - 1; // Make 0-indexed
    const rowMask = computeRowMask(position.columnSpan, columnIndex);

    const startingRowIndex = position.row - 1; // Make 0-indexed
    const endingRowIndex = startingRowIndex + position.rowSpan;
    for (let rowIndex = startingRowIndex; rowIndex < endingRowIndex; ++rowIndex) {
        if (!newGrid[rowIndex]) {
            newGrid[rowIndex] = 0;
        }

        newGrid[rowIndex] = newGrid[rowIndex] | rowMask;
    }

    return newGrid;
}

/**
 * Returns a bit field representation of a grid.
 * Each number in the returned array of numbers is a row in the grid.
 * A number is a bit mask indicating which cells of the row are filled.
 *
 * Because bitwise operations in JavaScript operate on signed 32-bit integers,
 * this grid can only go to 32 columns.
 */
function createFilledGridCellsBitsArray(positions: GridItemPosition[]): number[] {
    let grid: number[] = [];

    // Fill grid with positions
    for (let position of positions) {
        if (isPositionOnGrid(position)) {
            grid = fillGridWithPosition(position, grid);
        }
    }

    // Assign empty rows to any gaps
    for (let row = 0; row < grid.length; ++row) {
        if (!grid[row]) {
            grid[row] = 0;
        }
    }

    return grid;
}
